import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import CustomModal from '../shared/CustomModal/CustomModal'
import {
  BANNER_CHANNELS,
  CREATE_BANNER,
  DELETE_BANNER,
  formatBannerChannel,
  formatBannerDismissedAt,
  formatBannerEndsAt,
  formatBannerScheduledAt,
  fromBannerDateTimeLocal,
  GET_BANNER_DISMISSALS,
  GET_BANNER_LINK_CLICKS,
  GET_BANNERS,
  nowBannerDateTimeLocal,
  SET_BANNER_ACTIVE,
  toBannerDateTimeLocal,
  UPDATE_BANNER,
} from '../../graphql/banners'
import { ChannelPreview } from '../banners/BannerPreviews'
import './BannersList.css'

const defaultFormState = () => ({
  title: '',
  message: '',
  channel: 'BANNER',
  isActive: false,
  scheduledAt: nowBannerDateTimeLocal(),
  endsAt: '',
  linkUrl: '',
  linkLabel: '',
})

function StatusBadge({ isActive }) {
  return (
    <span className={`status-badge ${isActive ? 'activo' : 'desactivado'}`}>
      {isActive ? 'Activo' : 'Inactivo'}
    </span>
  )
}

function BannerMessageCell({ banner }) {
  const linkLabel = banner.linkLabel?.trim() || 'Ver más'

  return (
    <div className="banner-message-cell">
      {banner.title ? <strong>{banner.title}</strong> : null}
      <span>
        {banner.message}
        {banner.linkUrl?.trim() ? (
          <>
            {banner.message ? ' ' : null}
            <a
              href={banner.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="banner-message-cell__link"
            >
              {linkLabel}
            </a>
          </>
        ) : null}
      </span>
    </div>
  )
}

function BannerFormContent({ form, setForm }) {
  return (
    <div className="banners-section__form">
      <label className="banners-section__field">
        <span>Título (opcional)</span>
        <input
          type="text"
          value={form.title}
          onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
          placeholder="Ej: Aviso importante"
        />
      </label>
      <label className="banners-section__field">
        <span>Mensaje</span>
        <textarea
          rows={3}
          value={form.message}
          onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
          placeholder="Ej: El SII está presentando inestabilidad..."
        />
      </label>
      <div className="banners-section__field">
        <span>Canal</span>
        <div className="banners-section__channels">
          {BANNER_CHANNELS.map((item) => (
            <label
              key={item.value}
              className={`banners-section__channel ${form.channel === item.value ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="banner-channel"
                className="banners-section__channel-input"
                checked={form.channel === item.value}
                onChange={() => setForm((prev) => ({ ...prev, channel: item.value }))}
              />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      </div>
      <label className="banners-section__field">
        <span>Fecha y hora de inicio (Chile)</span>
        <input
          type="datetime-local"
          value={form.scheduledAt}
          onChange={(event) => setForm((prev) => ({ ...prev, scheduledAt: event.target.value }))}
        />
      </label>
      <label className="banners-section__field">
        <span>Fecha y hora de término (Chile, opcional)</span>
        <input
          type="datetime-local"
          value={form.endsAt}
          onChange={(event) => setForm((prev) => ({ ...prev, endsAt: event.target.value }))}
        />
      </label>
      <label className="banners-section__field">
        <span>Enlace (opcional)</span>
        <input
          type="url"
          value={form.linkUrl}
          onChange={(event) => setForm((prev) => ({ ...prev, linkUrl: event.target.value }))}
          placeholder="https://..."
        />
      </label>
      <label className="banners-section__field">
        <span>Texto del enlace (opcional)</span>
        <input
          type="text"
          value={form.linkLabel}
          onChange={(event) => setForm((prev) => ({ ...prev, linkLabel: event.target.value }))}
          placeholder="Ej: Ver más información"
        />
      </label>
      <label className="banners-section__checkbox">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
        />
        Activar al guardar
      </label>
      <div className="banners-section__field">
        <span>Vista previa</span>
        <ChannelPreview
          channel={form.channel}
          title={form.title}
          message={form.message}
          linkUrl={form.linkUrl}
          linkLabel={form.linkLabel}
        />
      </div>
    </div>
  )
}

export function BannersList() {
  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: GET_BANNERS,
    requestPolicy: 'network-only',
  })
  const [, createBanner] = useMutation(CREATE_BANNER)
  const [, updateBanner] = useMutation(UPDATE_BANNER)
  const [, deleteBanner] = useMutation(DELETE_BANNER)
  const [, setBannerActive] = useMutation(SET_BANNER_ACTIVE)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingBanner, setEditingBanner] = useState(null)
  const [form, setForm] = useState(defaultFormState())
  const [deleteModal, setDeleteModal] = useState({ open: false, banner: null })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [dismissalsModal, setDismissalsModal] = useState({ open: false, banner: null, tab: 'dismissals' })

  const sortedBanners = useMemo(
    () => [...(data?.banners || [])].sort(
      (a, b) => new Date(b.scheduledAt || 0) - new Date(a.scheduledAt || 0),
    ),
    [data?.banners],
  )

  const showFeedback = (type, message) => {
    setFeedback({ type, message })
    window.setTimeout(() => setFeedback(null), 3500)
  }

  const openCreateModal = () => {
    setEditingBanner(null)
    setForm(defaultFormState())
    setModalOpen(true)
  }

  const openEditModal = (banner) => {
    setEditingBanner(banner)
    setForm({
      title: banner.title || '',
      message: banner.message || '',
      channel: banner.channel || 'BANNER',
      isActive: Boolean(banner.isActive),
      scheduledAt: toBannerDateTimeLocal(banner.scheduledAt) || defaultFormState().scheduledAt,
      endsAt: toBannerDateTimeLocal(banner.endsAt) || '',
      linkUrl: banner.linkUrl || '',
      linkLabel: banner.linkLabel || '',
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingBanner(null)
    setForm(defaultFormState())
  }

  const buildInput = () => ({
    title: form.title?.trim() || undefined,
    message: form.message.trim(),
    template: 'WARNING',
    channel: form.channel,
    isActive: Boolean(form.isActive),
    scheduledAt: fromBannerDateTimeLocal(form.scheduledAt),
    endsAt: form.endsAt ? fromBannerDateTimeLocal(form.endsAt) : undefined,
    linkUrl: form.linkUrl?.trim() || undefined,
    linkLabel: form.linkLabel?.trim() || undefined,
  })

  const handleSave = async () => {
    if (!form.message.trim()) {
      showFeedback('error', 'El mensaje es obligatorio.')
      return
    }
    if (!form.scheduledAt) {
      showFeedback('error', 'Debes indicar la fecha y hora de inicio.')
      return
    }
    if (form.endsAt && form.endsAt <= form.scheduledAt) {
      showFeedback('error', 'La fecha de término debe ser posterior al inicio.')
      return
    }

    setSaving(true)
    try {
      const input = buildInput()
      if (editingBanner?.id) {
        if (!form.endsAt) input.endsAt = null
      }
      const result = editingBanner?.id
        ? await updateBanner({ input: { id: editingBanner.id, ...input } })
        : await createBanner({ input })

      if (result.error) throw result.error
      showFeedback('success', editingBanner ? 'Banner actualizado.' : 'Banner creado.')
      closeModal()
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo guardar el banner.')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (banner) => {
    const result = await setBannerActive({ id: banner.id, isActive: !banner.isActive })
    if (result.error) {
      showFeedback('error', result.error.message || 'No se pudo cambiar el estado.')
      return
    }
    showFeedback('success', banner.isActive ? 'Banner desactivado.' : 'Banner activado.')
    reexecuteQuery({ requestPolicy: 'network-only' })
  }

  const handleDelete = async () => {
    if (!deleteModal.banner?.id) return
    setDeleting(true)
    try {
      const result = await deleteBanner({ id: deleteModal.banner.id })
      if (result.error) throw result.error
      showFeedback('success', 'Banner eliminado.')
      setDeleteModal({ open: false, banner: null })
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo eliminar el banner.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Comunicaciones</h1>
        <p className="page-subtitle">
          Banners y avisos globales visibles para todos los usuarios del portal.
        </p>
      </div>

      {feedback ? (
        <div className={`alert ${feedback.type}`}>{feedback.message}</div>
      ) : null}

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Banners configurados</h3>
          <button type="button" className="btn btn-primary banners-new-btn" onClick={openCreateModal}>
            + Nuevo banner
          </button>
        </div>
        <div className="card-body">
          {fetching ? (
            <div className="loading-spinner">
              <div className="spinner" />
            </div>
          ) : sortedBanners.length === 0 ? (
            <div className="empty-state">
              <p>No hay banners configurados.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>MENSAJE</th>
                  <th>CANAL</th>
                  <th>ESTADO</th>
                  <th>INICIO (CHILE)</th>
                  <th>TÉRMINO (CHILE)</th>
                  <th>CIERRES</th>
                  <th>ENLACES</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {sortedBanners.map((banner) => (
                  <tr key={banner.id}>
                    <td>
                      <BannerMessageCell banner={banner} />
                    </td>
                    <td>{formatBannerChannel(banner.channel)}</td>
                    <td><StatusBadge isActive={banner.isActive} /></td>
                    <td>{formatBannerScheduledAt(banner.scheduledAt)}</td>
                    <td>{formatBannerEndsAt(banner.endsAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDismissalsModal({ open: true, banner, tab: 'dismissals' })}
                        disabled={!banner.dismissalCount}
                      >
                        {banner.dismissalCount || 0}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDismissalsModal({ open: true, banner, tab: 'links' })}
                        disabled={!banner.linkClickCount}
                      >
                        {banner.linkClickCount || 0}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(banner)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${banner.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => handleToggleActive(banner)}
                        >
                          {banner.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setDeleteModal({ open: true, banner })}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CustomModal
        open={modalOpen}
        onClose={closeModal}
        title={editingBanner ? 'Editar banner' : 'Nuevo banner'}
        size="large"
        classNameModal="custom-modal--banners"
        classNameContent="scfo-modal-form-body banners-section__modal-content"
        content={<BannerFormContent form={form} setForm={setForm} />}
        onConfirm={handleSave}
        confirmLabel={editingBanner ? 'Guardar cambios' : 'Crear banner'}
        confirmDisabled={saving}
        isLoading={saving}
      />

      <BannerHistoryModal
        open={dismissalsModal.open}
        banner={dismissalsModal.banner}
        initialTab={dismissalsModal.tab}
        onClose={() => setDismissalsModal({ open: false, banner: null, tab: 'dismissals' })}
      />

      <CustomModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, banner: null })}
        title="Eliminar banner"
        size="tiny"
        content={(
          <p className="banners-delete-text">
            ¿Eliminar el banner
            {' '}
            <strong>{deleteModal.banner?.title || deleteModal.banner?.message}</strong>
            ?
          </p>
        )}
        onConfirm={handleDelete}
        confirmLabel="Eliminar"
        confirmVariant="danger"
        confirmDisabled={deleting}
        isLoading={deleting}
      />
    </>
  )
}

function BannerHistoryModal({ open, banner, initialTab = 'dismissals', onClose }) {
  const [activeTab, setActiveTab] = useState(initialTab)

  const [{ data: dismissalsData, fetching: fetchingDismissals, error: dismissalsError }] = useQuery({
    query: GET_BANNER_DISMISSALS,
    variables: { bannerId: banner?.id },
    pause: !open || !banner?.id,
    requestPolicy: 'network-only',
  })
  const [{ data: linkClicksData, fetching: fetchingLinkClicks, error: linkClicksError }] = useQuery({
    query: GET_BANNER_LINK_CLICKS,
    variables: { bannerId: banner?.id },
    pause: !open || !banner?.id,
    requestPolicy: 'network-only',
  })

  const dismissals = dismissalsData?.bannerDismissals || []
  const linkClicks = linkClicksData?.bannerLinkClicks || []
  const fetching = fetchingDismissals || fetchingLinkClicks
  const error = dismissalsError || linkClicksError

  useEffect(() => {
    if (open) setActiveTab(initialTab)
  }, [open, initialTab, banner?.id])

  const bannerTitle = banner?.title?.trim() || 'Sin título'
  const bannerMessage = banner?.message?.trim()

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title="Historial del banner"
      size="large"
      classNameModal="custom-modal--banners"
      content={(
        <div className="banners-history-modal">
          <div className="banners-history-modal__banner">
            <p className="banners-history-modal__banner-title">{bannerTitle}</p>
            {bannerMessage ? (
              <p className="banners-history-modal__banner-message">{bannerMessage}</p>
            ) : null}
            <div className="banners-history-modal__meta">
              <span className="banners-history-modal__meta-item">
                Canal:
                {' '}
                <strong>{formatBannerChannel(banner?.channel)}</strong>
              </span>
              <span className="banners-history-modal__meta-divider" aria-hidden="true" />
              <span className="banners-history-modal__meta-item">
                Cierres:
                {' '}
                <strong>{dismissals.length}</strong>
              </span>
              <span className="banners-history-modal__meta-divider" aria-hidden="true" />
              <span className="banners-history-modal__meta-item">
                Enlaces:
                {' '}
                <strong>{linkClicks.length}</strong>
              </span>
            </div>
          </div>

          {fetching ? (
            <p className="banners-history-modal__loading">Cargando historial…</p>
          ) : error ? (
            <p className="banners-dismissals-modal__error">
              {error.graphQLErrors?.[0]?.message || error.message || 'No se pudo cargar el historial.'}
            </p>
          ) : (
            <>
              <div className="banners-history-modal__tabs" role="tablist" aria-label="Tipo de interacción">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'dismissals'}
                  className={`banners-history-modal__tab ${activeTab === 'dismissals' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('dismissals')}
                >
                  Cierres del aviso
                  <span className="banners-history-modal__tab-count">{dismissals.length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'links'}
                  className={`banners-history-modal__tab ${activeTab === 'links' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('links')}
                >
                  Clics en enlace
                  <span className="banners-history-modal__tab-count">{linkClicks.length}</span>
                </button>
              </div>

              <div className="banners-history-modal__panel" role="tabpanel">
                {activeTab === 'dismissals' ? (
                  dismissals.length === 0 ? (
                    <p className="banners-history-section__empty">No hay cierres registrados.</p>
                  ) : (
                    <table className="data-table banners-dismissals-table">
                      <thead>
                        <tr>
                          <th>USUARIO</th>
                          <th>EMPRESA</th>
                          <th>CANAL</th>
                          <th>FECHA CIERRE (CHILE)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dismissals.map((entry) => (
                          <tr key={entry.id}>
                            <td>{entry.userEmail || entry.userId}</td>
                            <td>{entry.companyName || entry.companyId || '—'}</td>
                            <td>{formatBannerChannel(entry.lastDismissedChannel || entry.dismissedChannels?.[0])}</td>
                            <td>{formatBannerDismissedAt(entry.dismissedAt || entry.updatedAt || entry.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : linkClicks.length === 0 ? (
                  <p className="banners-history-section__empty">No hay clics en enlace registrados.</p>
                ) : (
                  <table className="data-table banners-dismissals-table">
                    <thead>
                      <tr>
                        <th>USUARIO</th>
                        <th>EMPRESA</th>
                        <th>ENLACE</th>
                        <th>CANAL</th>
                        <th>FECHA CLICK (CHILE)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkClicks.map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.userEmail || entry.userId}</td>
                          <td>{entry.companyName || entry.companyId || '—'}</td>
                          <td>
                            <span className="banners-history-modal__link-label">
                              {entry.linkLabel || entry.linkUrl}
                            </span>
                            {entry.linkLabel && entry.linkUrl ? (
                              <span className="banners-history-modal__link-url">{entry.linkUrl}</span>
                            ) : null}
                          </td>
                          <td>{formatBannerChannel(entry.channel)}</td>
                          <td>{formatBannerDismissedAt(entry.clickedAt || entry.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}
      customActions={(
        <div className="banners-dismissals-modal__actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>
            Cerrar
          </button>
        </div>
      )}
    />
  )
}

export default BannersList
